import React, { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styles from './GearCard.module.css';
import { useTranslation } from 'react-i18next';
import { formatCurrencyAmount, formatEuro } from '../utils/formatCurrency';
import { getPriceSettings } from '../utils/priceSettings';
import { addToCompare, clearCompare, COMPARE_LIMIT, isInCompare, removeFromCompare } from '../utils/gearCompare';

const parsePrice = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const parsed = Number.parseFloat(String(value || '').replace(',', '.').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(parsed) ? parsed : null;
};

function GearCard({ gear }) {
  const [compareFeedback, setCompareFeedback] = useState(null);
  const [isAdded, setIsAdded] = useState(() => isInCompare(gear?.id));

  // Sync state if returned from compare page
  useEffect(() => {
    setIsAdded(isInCompare(gear?.id));
  }, [gear?.id]);

  const handleAddToCompare = (e) => {
    e.preventDefault();
    if (!gear?.id) return;

    if (isAdded) {
      removeFromCompare(gear.id);
      setIsAdded(false);
      return;
    }

    const result = addToCompare(gear.id, { category: gear.category });

    if (result.ok) {
      setIsAdded(true);
    } else if (result.reason === 'incompatible') {
      const keep = window.confirm(`You can only compare items from the same category (${result.compareCategory || '-'}). Delete current compare items and add this one?`);
      if (keep) {
        clearCompare();
        const retryResult = addToCompare(gear.id, { category: gear.category });
        if (retryResult.ok) {
          setIsAdded(true);
        }
      }
    } else if (result.reason === 'limit') {
      setCompareFeedback('limit');
      setTimeout(() => setCompareFeedback(null), 2000);
    } else if (result.reason === 'exists') {
      setIsAdded(true);
    }
  };

  const { i18n } = useTranslation();
  const settings = getPriceSettings();
  const normalizedOffers = Array.isArray(gear?.offers)
    ? gear.offers
      .map((offer) => ({
        ...offer,
        currency: String(offer?.currency || gear?.currency || 'EUR').toUpperCase(),
        price: parsePrice(offer?.price),
        approxPriceEUR: parsePrice(offer?.approxPriceEUR),
        approxPriceInTargetCurrency: parsePrice(offer?.approxPriceInTargetCurrency),
      }))
      .filter((offer) => Number.isFinite(offer.price))
    : [];

  const filteredOffers = normalizedOffers.filter((offer) => settings.selectedPriceCurrencies.includes(offer.currency));
  const offersForDisplay = settings.viewMode === 'prices' && filteredOffers.length > 0
    ? filteredOffers
    : normalizedOffers;

  const cheapestOriginalOffer = offersForDisplay
    .slice()
    .sort((a, b) => a.price - b.price)[0] || null;

  const cheapestTargetOffer = offersForDisplay
    .filter((offer) => Number.isFinite(offer.approxPriceInTargetCurrency))
    .slice()
    .sort((a, b) => a.approxPriceInTargetCurrency - b.approxPriceInTargetCurrency)[0] || null;

  const originalCurrency = String(gear?.currency || 'EUR').toUpperCase();
  const originalPrice = parsePrice(gear?.price);

  let mainPrice = formatEuro(gear.price, i18n.language);
  let subPrice = '';

  if (settings.viewMode === 'prices') {
    if (cheapestOriginalOffer) {
      mainPrice = formatCurrencyAmount(cheapestOriginalOffer.price, cheapestOriginalOffer.currency, i18n.language);
      subPrice = '';
    } else if (Number.isFinite(originalPrice)) {
      mainPrice = formatCurrencyAmount(originalPrice, originalCurrency, i18n.language);
    }
  } else {
    const targetCurrency = settings.exchangeTargetCurrency;
    if (cheapestTargetOffer) {
      mainPrice = formatCurrencyAmount(cheapestTargetOffer.approxPriceInTargetCurrency, targetCurrency, i18n.language);
      subPrice = formatCurrencyAmount(cheapestTargetOffer.price, cheapestTargetOffer.currency, i18n.language);
    } else if (Number.isFinite(parsePrice(gear?.priceComparison?.approxPriceInTargetCurrency))) {
      mainPrice = formatCurrencyAmount(gear.priceComparison.approxPriceInTargetCurrency, targetCurrency, i18n.language);
      subPrice = Number.isFinite(originalPrice)
        ? formatCurrencyAmount(originalPrice, originalCurrency, i18n.language)
        : '';
    }
  }

  const genderLower = String(gear?.gender || '').toLowerCase();
  const nameLower = String(gear?.name || '').toLowerCase();
  const isWomensProduct = genderLower.includes('women') || genderLower.includes('female') || genderLower === 'w' || genderLower.includes('donna') || genderLower.includes('femme') || nameLower.includes("women's") || nameLower.includes(" womens") || nameLower.endsWith(" w");

  const catLower = String(gear?.category || '').toLowerCase();
  const equipmentKeywords = ['backpack', 'pole', 'head lamp', 'headlamp', 'location', 'ice axe', 'crampon', 'helmet', 'harness', 'hardware', 'rescue', 'rope', 'eyewear', 'glasses', 'tent', 'sleeping', 'pad', 'climbing'];
  const isEquipment = equipmentKeywords.some(kw => catLower.includes(kw));
  
  const showWomensFrame = isEquipment && isWomensProduct;

  let maxDiscount = 0;
  
  // Also perform a quick string-to-number parse matching the format
  const parseCurrencyString = (val) => {
    const str = String(val || '');
    const num = parsePrice(str);
    const symbol = str.replace(/[\d.,-\s]/g, '').trim().toUpperCase();
    return { num, symbol };
  };

  const topLevelDiscount = parsePrice(gear?.saleDiscountPercent);
  if (Number.isFinite(topLevelDiscount) && topLevelDiscount > maxDiscount) maxDiscount = topLevelDiscount;

  const topOldData = parseCurrencyString(gear?.oldPrice);
  const topNewData = parseCurrencyString(gear?.price);
  if (Number.isFinite(topOldData.num) && Number.isFinite(topNewData.num) && topOldData.num > 0 && topNewData.num < topOldData.num) {
    if (!topOldData.symbol || !topNewData.symbol || topOldData.symbol === topNewData.symbol) {
       const calculated = ((topOldData.num - topNewData.num) / topOldData.num) * 100;
       if (calculated > maxDiscount) maxDiscount = calculated;
    }
  }

  if (Array.isArray(gear?.offers)) {
    gear.offers.forEach(offer => {
      const offerDiscount = parsePrice(offer?.saleDiscountPercent);
      if (Number.isFinite(offerDiscount) && offerDiscount > maxDiscount) {
        maxDiscount = offerDiscount;
      }
      
      const offerOld = parseCurrencyString(offer?.oldPrice);
      const offerNew = parseCurrencyString(offer?.price);
      if (Number.isFinite(offerOld.num) && Number.isFinite(offerNew.num) && offerOld.num > 0 && offerNew.num < offerOld.num) {
        const offerOldSymbol = offerOld.symbol || String(offer?.oldPriceCurrency || offer?.currency || '').toUpperCase();
        const offerNewSymbol = offerNew.symbol || String(offer?.currency || '').toUpperCase();
        if (!offerOldSymbol || !offerNewSymbol || offerOldSymbol === offerNewSymbol) {
          const calculated = ((offerOld.num - offerNew.num) / offerOld.num) * 100;
          if (calculated > maxDiscount) maxDiscount = calculated;
        }
      }
    });
  }

  let frameClass = '';
  let discountColorClass = '';
  const isDiscounted = maxDiscount > 0;
  
  if (isDiscounted) {
    if (maxDiscount < 5) {
      frameClass = styles.discountFrameGrey;
      discountColorClass = styles.discountTitleGrey;
    } else if (maxDiscount < 15) {
      frameClass = styles.discountFrameBlue;
      discountColorClass = styles.discountTitleBlue;
    } else if (maxDiscount < 25) {
      frameClass = styles.discountFrameYellow;
      discountColorClass = styles.discountTitleYellow;
    } else {
      frameClass = styles.discountFrameRed;
      discountColorClass = styles.discountTitleRed;
    }
  }

  // We removed womensEquipmentCard frame handling here to use an icon wrapper instead.

  const content = (
    <div className={`${styles.gearCard} ${frameClass}`}>
      {isDiscounted && (
        <div className={discountColorClass}>
          {Math.round(maxDiscount)}% Sale
        </div>
      )}
      {gear.image && (
        <div className={styles.imageWrapper}>
          <img src={gear.image} alt={gear.name} className={styles.gearImg} />
          {showWomensFrame && (
            <div className={styles.womensIcon} title="Women's specific gear">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="9" r="6"></circle>
                <path d="M12 15v7"></path>
                <path d="M9 19h6"></path>
              </svg>
            </div>
          )}
        </div>
      )}
      <div className={styles.brand}>{gear.brand}</div>
      <h4 className={styles.title} title={gear.name}>{gear.name}</h4>
      <div className={styles.price}>{mainPrice}</div>
      <div className={styles.metricsOverlay} aria-hidden>
        <div className={styles.overlayProductName} title={gear.name}>
          {gear.name}
        </div>
        {(() => {
          const blacklist = new Set(['id','name','brand','price','category','description','image','imageUrl','offers','merchant','link','validatedAt','originalId','priceComparison','currency']);
          const looksLikeUrl = (v) => typeof v === 'string' && (/https?:\/\//i.test(v) || v.includes('www.') || v.includes('.com') || v.includes('.html'));
          const looksLikeLongText = (v) => typeof v === 'string' && v.length > 60;
          const containsHtmlLink = (v) => typeof v === 'string' && /<a\s+href/i.test(v);

          const category = String(gear?.category || '').toLowerCase();
          const priorityMap = {
            backpacks: { keys: ['capacity', 'capacity_liters', 'intended_activity', 'activity'], exact: false },
            footwear: { keys: ['weight', 'upper_material'], exact: true },
            shoes: { keys: ['weight', 'upper_material'], exact: true },
            boots: { keys: ['weight', 'upper_material'], exact: true },
            'hiking boots': { keys: ['weight', 'upper_material'], exact: true },
            'hiking footwear': { keys: ['weight', 'upper_material'], exact: true },
            'hard shell': { keys: ['waterproofness', 'breathability'], exact: false },
            hardshell: { keys: ['waterproofness', 'breathability'], exact: false },
            'soft shell': { keys: ['weight', 'water_resistance', 'waterproof', 'windproof'], exact: false },
            softshell: { keys: ['weight', 'water_resistance', 'waterproof', 'windproof'], exact: false },
            fleece: { keys: ['weight'], exact: true },
            'base layer': { keys: ['weight', 'material', 'material_composition'], exact: false },
            baselayer: { keys: ['weight', 'material', 'material_composition'], exact: false },
            down: { keys: ['fill_power', 'fill_weight'], exact: false },
            tents: { keys: ['min_weight', 'person_capacity', 'waterhead_rating'], exact: false },
            'trekking poles': { keys: ['weight', 'collapsed_length'], exact: false },
            crampons: { keys: ['weight', 'points'], exact: false },
            'ice axe': { keys: ['weight', 'rating', 'technical_rating'], exact: false },
            headlamps: { keys: ['weight', 'lumens', 'max_lumens'], exact: false },
            'head lamps': { keys: ['weight', 'lumens', 'max_lumens'], exact: false },
            helmets: { keys: ['weight', 'material', 'safety_rating'], exact: false },
            harnesses: { keys: ['weight', 'gear_loops', 'type'], exact: false },
            ropes: { keys: ['weight', 'length', 'diameter', 'type'], exact: false },
            location: { keys: ['weight', 'battery_life', 'type'], exact: false },
            hardware: { keys: ['weight', 'strength', 'material'], exact: false },
            eyewear: { keys: ['lens_category', 'category'], exact: true },
            glasses: { keys: ['lens_category', 'category'], exact: true }
          };

          const fallback = ['weight', 'capacity', 'waterproofness', 'breathability', 'fill_power'];

          const findConfig = (map) => {
            for (const key of Object.keys(map)) {
              if (category.includes(key)) return map[key];
            }
            return null;
          };

          const config = findConfig(priorityMap);
          const priorities = config ? config.keys : fallback;
          const exact = config ? config.exact : false;
          const selected = [];

          const pushIfValid = (k) => {
            if (exact && selected.length >= priorities.length) return;
            if (!exact && selected.length >= 2) return;
            if (blacklist.has(k)) return;
            if (selected.some(s => s.key === k)) return; // prevent duplicates
            const val = gear?.[k];
            if (val === undefined || val === null) return;
            if (looksLikeUrl(val) || containsHtmlLink(val)) return;
            if (typeof val === 'string' && !val.trim()) return;
            if (looksLikeLongText(val)) return;
            selected.push({ key: k, value: val });
          };

          for (const k of priorities) pushIfValid(k);
          if (!exact && selected.length < 2) {
            // scan common useful keys in predictable order
            const common = ['weight','capacity','fill_power','fill_weight','waterproofness','breathability','boot_category','crampon_compat','material'];
            for (const k of common) {
              if (selected.length >= 2) break;
              pushIfValid(k);
            }
          }

          if (!exact && selected.length < 2) {
            for (const k of Object.keys(gear || {})) {
              if (selected.length >= 2) break;
              if (String(k).toLowerCase().includes('url') || String(k).toLowerCase().includes('link')) continue;
              pushIfValid(k);
            }
          }

          const human = (s) => String(s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
          const formatVal = (v, k) => {
            let formatted = v;
            if (Array.isArray(v)) {
              formatted = v.join(', ');
            } else if (typeof v === 'boolean') {
              formatted = v ? 'Yes' : 'No';
            } else {
              formatted = String(v);
            }
            
            if ((k === 'capacity' || k === 'capacity_liters') && /^\d+$/.test(formatted)) {
              return formatted + ' L';
            }
            if ((k === 'water_resistance' || k === 'waterproof') && (formatted.toLowerCase() === 'true' || formatted.toLowerCase() === 'false')) {
              return formatted.toLowerCase() === 'true' ? 'Yes' : 'No';
            }
            return formatted;
          };

          if (!selected.length) {
            return (
              <>
                <div style={{ marginTop: 'auto' }}></div>
                <button
                  type="button"
                  className={`${styles.compareQuickBtn} ${isAdded ? styles.added : ''}`}
                  onClick={handleAddToCompare}
                  title={isAdded ? "Remove from compare" : "Add to compare"}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    {isAdded ? (
                      <polyline points="20 6 9 17 4 12"></polyline>
                    ) : (
                      <>
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                      </>
                    )}
                  </svg>
                  <span>
                    {compareFeedback === 'limit' ? 'Limit reached' 
                    : compareFeedback === 'exists' ? 'Already added' 
                    : isAdded ? 'Added!' 
                    : 'Compare'}
                  </span>
                </button>
              </>
            );
          }

          return (
            <>
              {selected.map((m) => (
                <div key={m.key} className={styles.metricRow} title={m.key}>
                  <strong>{human(m.key)}:</strong>&nbsp;<span>{formatVal(m.value, m.key)}</span>
                </div>
              ))}
              <div style={{ marginTop: 'auto' }}></div>
              <button
                type="button"
                className={`${styles.compareQuickBtn} ${isAdded ? styles.added : ''}`}
                onClick={handleAddToCompare}
                title={isAdded ? "Remove from compare" : "Add to compare"}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {isAdded ? (
                    <polyline points="20 6 9 17 4 12"></polyline>
                  ) : (
                    <>
                      <line x1="12" y1="5" x2="12" y2="19"></line>
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                    </>
                  )}
                </svg>
                <span>
                  {compareFeedback === 'limit' ? 'Limit reached' 
                  : compareFeedback === 'exists' ? 'Already added' 
                  : isAdded ? 'Added!' 
                  : 'Compare'}
                </span>
              </button>
            </>
          );
        })()}
      </div>
      {subPrice && <div className={styles.originalPrice}>{subPrice}</div>}
    </div>
  );

  return (
    <Link to={`/gear/${gear.id}`} className={styles.cardLink} style={{ textDecoration: 'none', color: 'inherit' }}>
      {content}
    </Link>
  );
}

export default GearCard;
